import {
  extractRangeConstraints,
  getConstraintValue,
} from "./usePanelResize.constraints";
import type { UsePanelResizeParams } from "./usePanelResize.types";

/**
 * Calculates new flex values from mouse delta (proportional resize + flex-grow constraints).
 * Pixel constraints are applied separately in updateFlexValues after DOM measurement.
 */
export function calculateFlexValuesFromDelta(
  delta: number,
  totalSize: number,
  initialValues: [number, number],
  range: UsePanelResizeParams["range"] | undefined,
  dragSensitivity: number,
  minFlex: number
): [number, number] {
  if (totalSize <= 0) {
    return initialValues;
  }

  const totalFlex = initialValues[0] + initialValues[1];
  const flexDelta = (delta / totalSize) * totalFlex * dragSensitivity;

  let newFirstFlex = initialValues[0] + flexDelta;
  let newSecondFlex = initialValues[1] - flexDelta;

  if (range) {
    const { firstMin, secondMin, firstMax, secondMax } =
      extractRangeConstraints(range);

    const firstMinVal = getConstraintValue(firstMin);
    const firstMaxVal = getConstraintValue(firstMax);
    const secondMinVal = getConstraintValue(secondMin);
    const secondMaxVal = getConstraintValue(secondMax);

    if (firstMinVal && !firstMinVal.isPixel) {
      const clamped = Math.max(newFirstFlex, firstMinVal.value);
      newSecondFlex -= clamped - newFirstFlex;
      newFirstFlex = clamped;
    }
    if (firstMaxVal && !firstMaxVal.isPixel) {
      const clamped = Math.min(newFirstFlex, firstMaxVal.value);
      newSecondFlex -= clamped - newFirstFlex;
      newFirstFlex = clamped;
    }

    if (secondMinVal && !secondMinVal.isPixel) {
      const clamped = Math.max(newSecondFlex, secondMinVal.value);
      newFirstFlex -= clamped - newSecondFlex;
      newSecondFlex = clamped;
    }
    if (secondMaxVal && !secondMaxVal.isPixel) {
      const clamped = Math.min(newSecondFlex, secondMaxVal.value);
      newFirstFlex -= clamped - newSecondFlex;
      newSecondFlex = clamped;
    }
  }

  if (newFirstFlex <= 0) {
    newFirstFlex = minFlex;
  }
  if (newSecondFlex <= 0) {
    newSecondFlex = minFlex;
  }

  return [newFirstFlex, newSecondFlex];
}
